import type { KeyPrefix } from 'i18next'
import type { StronglyTypedUseTranslation } from './useTranslation'
import { useFeatureFlag } from '@/composables/featureFlags.js'
import useTranslation from '@/composables/useTranslation.js'

const VETERA_ENVIRONMENTS = ['dach']

/**
 * Returns whether Vetera branding is active for the current context.
 *
 * Priority:
 *   1. `temporary.vetera_branding` feature flag (per-org, overrides env)
 *   2. `NUXT_PUBLIC_ENVIRONMENT_NAME` env var — any value in VETERA_ENVIRONMENTS
 *      enables Vetera branding deployment-wide (e.g. `dach`)
 */
export function useIsVetera() {
  const isFlagEnabled = useFeatureFlag('temporary.vetera_branding')
  const config = useRuntimeConfig()

  return computed(
    () =>
      isFlagEnabled.value ||
      VETERA_ENVIRONMENTS.includes(
        config.public.environmentName?.toLowerCase() ?? '',
      ),
  )
}

/**
 * Set tenant-scoped favicon and manifest `<link>` tags so the browser
 * fetches brand-aware assets via Django's `/<provetId>/favicon.*` and
 * `/<provetId>/manifest.json` routes.
 *
 * Falls back to root paths when no provetId is available.
 * Skipped in FE Review App builds (hash mode).
 */
export function useBrandHead() {
  const config = useRuntimeConfig()
  if (config.public.routerHashMode) {
    return
  }

  const provetId = useRouteParams('provetId')

  useHead({
    link: computed(() => {
      const base = provetId.value ? `/${provetId.value}` : ''
      return [
        { rel: 'manifest', href: `${base}/manifest.json` },
        { rel: 'icon', href: `${base}/favicon.ico`, sizes: '48x48' },
        {
          rel: 'icon',
          href: `${base}/favicon.svg`,
          sizes: 'any',
          type: 'image/svg+xml',
        },
        { rel: 'apple-touch-icon', href: `${base}/apple-touch-icon.png` },
      ]
    }),
  })
}

/**
 * Brand-aware translation composable for Provet/Vetera multi-brand support.
 *
 * Automatically selects between base and `_vetera` suffixed translation keys
 * based on `useIsVetera()` (feature flag or env). Translation keys use
 * a suffix pattern:
 * - Base key: "someKey" (Provet version)
 * - Vetera key: "someKey_vetera" (Vetera version)
 *
 * @param keyPrefix - The translation key prefix (e.g., 'common.aiScribe.qrCodePreview')
 * @returns Translation function that handles brand selection automatically
 *
 * @example
 * ```vue
 * <script setup>
 * import { useBrandTranslation } from '@/composables/useBrand.js'
 * const tBrand = useBrandTranslation('common.aiScribe.qrCodePreview')
 * </script>
 *
 * <template>
 *   <p>{{ tBrand('alternativeRecordingText') }}</p>
 * </template>
 * ```
 */
export function useBrandTranslation<
  TPrefix extends KeyPrefix<'application'> = undefined,
>(keyPrefix?: TPrefix): StronglyTypedUseTranslation<TPrefix> {
  const isVetera = useIsVetera()
  const translate = useTranslation(keyPrefix)

  const t = ((key: string, ...args: unknown[]) => {
    const translationKey = isVetera.value ? `${key}_vetera` : key
    // Type assertion needed to avoid 'excessively deep type instantiation' error
    // when wrapping i18next's complex generic TFunction
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (translate as any)(translationKey, ...args)
  }) as unknown as StronglyTypedUseTranslation<TPrefix>

  return t
}
